import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, interval, of } from "rxjs";
import {
  switchMap,
  takeWhile,
  tap,
  catchError,
  startWith,
} from "rxjs/operators";

export interface PollingRequest {
  id: string;
  callbackUrl: string;
  type: string;
  metadata?: any;
  createdAt: number;
}

export interface PollingResponse {
  id: number;
  input_dem: number;
  output_dem: number | null;
  selected_patches: number[];
  job_finished: boolean;
  status_info: string;
  created_on: string;
  updated_on: string;
}

export interface ClippingPolygonJob {
  id: number;
  status: string;
  job_finished: boolean;
  input_raster: {
    id: number;
    label: string;
    geodata_type: string;
    url: string;
  };
  output_raster: any | null;
}

export type PollingResponseData = PollingResponse | ClippingPolygonJob[];

export interface PollingCompletionData {
  success: boolean;
  response: PollingResponseData;
  message: string;
  errorJobs?: ClippingPolygonJob[];
}

@Injectable({
  providedIn: "root",
})
export class PollingService {
  private readonly STORAGE_KEY = "polling_requests";
  private readonly POLLING_INTERVAL = 5000; // 5 seconds
  private activePolling = new Map<string, any>();

  constructor(private http: HttpClient) {
    this.initializePollingFromStorage();
  }

  /**
   * Add a new request to polling queue
   */
  addRequest(request: Omit<PollingRequest, "id" | "createdAt">): string {
    const id = this.generateRequestId();
    const pollingRequest: PollingRequest = {
      ...request,
      id,
      createdAt: Date.now(),
    };

    // Save to localStorage
    this.saveToStorage(pollingRequest);

    // Start polling
    this.startPolling(pollingRequest);

    return id;
  }

  /**
   * Remove a request from polling queue
   */
  removeRequest(id: string): void {
    // Stop polling
    if (this.activePolling.has(id)) {
      this.activePolling.get(id).unsubscribe();
      this.activePolling.delete(id);
    }

    // Remove from localStorage
    this.removeFromStorage(id);
  }

  /**
   * Get all active requests
   */
  getActiveRequests(): PollingRequest[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Check if there are any active requests
   */
  hasActiveRequests(): boolean {
    return this.getActiveRequests().length > 0;
  }

  /**
   * Get requests by type
   */
  getRequestsByType(type: string): PollingRequest[] {
    return this.getActiveRequests().filter((req) => req.type === type);
  }

  private generateRequestId(): string {
    return `poll_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private saveToStorage(request: PollingRequest): void {
    const requests = this.getActiveRequests();
    requests.push(request);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(requests));
  }

  private removeFromStorage(id: string): void {
    const requests = this.getActiveRequests().filter((req) => req.id !== id);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(requests));
  }

  private startPolling(request: PollingRequest): void {
    // Start with an immediate request, then continue with interval
    const polling$ = interval(this.POLLING_INTERVAL).pipe(
      startWith(0), // Start immediately
      switchMap(() => {
        return this.pollCallback(request.callbackUrl, request.type);
      }),
      takeWhile(
        (response: PollingResponseData) => {
          if (request.type === "clipping_polygon") {
            // For clipping polygon, check if all jobs are finished
            const jobs = response as ClippingPolygonJob[];
            if (!Array.isArray(jobs) || jobs.length === 0) {
              return false; // Stop if no jobs or invalid response
            }
            return jobs.some((job) => !job.job_finished);
          } else {
            // For other types, use the single response
            const singleResponse = response as PollingResponse;
            return singleResponse.job_finished === false;
          }
        },
        true // Include the last value that fails the condition
      ),
      tap((response: PollingResponseData) => {
        this.emitPollingStatusUpdate(request, response);

        // Check if all jobs are finished
        if (request.type === "clipping_polygon") {
          const jobs = response as ClippingPolygonJob[];
          if (
            Array.isArray(jobs) &&
            jobs.length > 0 &&
            jobs.every((job) => job.job_finished)
          ) {
            this.handlePollingComplete(request, response);
          }
        } else {
          const singleResponse = response as PollingResponse;
          if (singleResponse.job_finished === true) {
            this.handlePollingComplete(request, response);
          }
        }
      }),
      catchError((error) => {
        console.error("Polling error:", error);
        if (request.type === "clipping_polygon") {
          return of([]);
        } else {
          return of({
            id: 0,
            input_dem: 0,
            output_dem: null,
            selected_patches: [],
            job_finished: true,
            status_info: "Polling failed",
            created_on: "",
            updated_on: "",
          });
        }
      })
    );

    this.activePolling.set(request.id, polling$.subscribe());
  }

  private pollCallback(
    callbackUrl: string,
    type: string
  ): Observable<PollingResponseData> {
    if (type === "clipping_polygon") {
      return this.http.get<ClippingPolygonJob[]>(callbackUrl);
    } else {
      return this.http.get<PollingResponse>(callbackUrl);
    }
  }

  private handlePollingComplete(
    request: PollingRequest,
    response: PollingResponseData
  ): void {
    // Remove from polling queue
    this.removeRequest(request.id);

    // Determine if the job was successful
    let isSuccess = false;
    let message = "Job completed successfully";
    let errorJobs: ClippingPolygonJob[] = [];

    if (request.type === "clipping_polygon") {
      const jobs = response as ClippingPolygonJob[];
      if (Array.isArray(jobs) && jobs.length > 0) {
        // Check if all jobs succeeded
        isSuccess = jobs.every(
          (job) => job.job_finished && job.output_raster !== null
        );
        // Collect jobs with errors (finished but no output_raster or have error status)
        errorJobs = jobs.filter(
          (job) =>
            job.job_finished &&
            (job.output_raster === null ||
              (job.status && job.status.trim() !== ""))
        );
        message = isSuccess
          ? "All clipping jobs completed successfully"
          : errorJobs.length > 0
          ? errorJobs.map((job) => job.status || "Unknown error").join("; ")
          : "Some clipping jobs failed";
      }
    } else {
      const singleResponse = response as PollingResponse;
      isSuccess =
        singleResponse.job_finished && singleResponse.output_dem !== null;
      message = isSuccess
        ? "Job completed successfully"
        : singleResponse.status_info || "Job failed";
    }

    // Emit event for the specific request type
    this.emitPollingComplete(request, {
      success: isSuccess,
      response: response,
      message: message,
      errorJobs: errorJobs,
    });
  }

  private emitPollingComplete(
    request: PollingRequest,
    completionData: PollingCompletionData
  ): void {
    const event = new CustomEvent("pollingComplete", {
      detail: {
        request,
        completionData,
      },
    });
    window.dispatchEvent(event);
  }

  private emitPollingStatusUpdate(
    request: PollingRequest,
    response: PollingResponseData
  ): void {
    const event = new CustomEvent("pollingStatusUpdate", {
      detail: {
        request,
        response,
      },
    });
    window.dispatchEvent(event);
  }

  private initializePollingFromStorage(): void {
    const requests = this.getActiveRequests();

    // Filter out old requests (older than 24 hours)
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const validRequests = requests.filter((req) => req.createdAt > oneDayAgo);

    // Update localStorage with only valid requests
    if (validRequests.length !== requests.length) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(validRequests));
    }

    // Start polling for valid requests
    validRequests.forEach((request) => {
      this.startPolling(request);
    });
  }

  /**
   * Clean up old requests (called on app initialization)
   */
  cleanupOldRequests(): void {
    const requests = this.getActiveRequests();
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const validRequests = requests.filter((req) => req.createdAt > oneDayAgo);

    if (validRequests.length !== requests.length) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(validRequests));
    }
  }

  /**
   * Stop all polling (called on app destruction)
   */
  stopAllPolling(): void {
    this.activePolling.forEach((subscription) => subscription.unsubscribe());
    this.activePolling.clear();
  }
}
